<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <div :class="['dropdown-form-presenter', { 'is-invalid': validationData.isValid === false }]">
    <div class="d-flex">
      <span v-if="preInputLabel" class="dropdown-form-presenter__pre-input-lable">
        {{ preInputLabel }}
      </span>
      <zen-multiselect
        :id="data.name"
        :value="value"
        :options="options"
        :name="data.name"
        :placeholder="placeholder"
        :required="data.mandatory"
        :allow-empty="isEmptyAllowed"
        :close-on-select="!multiple"
        :taggable="taggable"
        :hide-selected="taggable"
        :multiple="multiple"
        :show-checkboxes="!taggable"
        :track-by="taggable ? 'label' : 'value'"
        label="label"
        searchable
        render-select-element
        :custom-label="isPersonDropdown ? getPersonFullLabel : undefined"
        :loading="loading"
        :disabled="disabled"
        @search-change="searchHandler"
        @tag="addTag"
        v-on="$listeners"
      >
        <template slot="afterList">
          <infinite-loading v-if="!isDropdownOptionsCompleted" spinner="spiral" @infinite="infiniteHandler">
            <span slot="no-results" />
            <span slot="no-more" />
          </infinite-loading>
        </template>
        <template v-if="isPersonDropdown && multiple" v-slot:tag="slotProps">
          <span class="multiselect__tag">
            <span v-b-tooltip.hover="getTooltipOptions(slotProps.option)">
              {{ getPersonLabel(slotProps.option) }}
            </span>
            <i
              aria-hidden="true"
              tabindex="1"
              class="multiselect__tag-icon"
              @keypress.enter.prevent="slotProps.remove(slotProps.option)"
              @mousedown.prevent="slotProps.remove(slotProps.option)"
            ></i>
          </span>
        </template>

        <template v-if="isPersonDropdown && !multiple" v-slot:singleLabel="slotProps">
          <span v-b-tooltip.hover="getTooltipOptions(slotProps.option)">
            {{ getPersonLabel(slotProps.option) }}
          </span>
        </template>
      </zen-multiselect>
    </div>

    <div v-if="validationData.isValid === false" class="is-invalid-error-message">
      {{ validationData.errorMessage }}
    </div>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';
import { get, isBoolean, isFunction, debounce, includes } from 'lodash';
import { VBTooltip } from 'bootstrap-vue';

import ZenMultiselect from '@reciprocity/multiselect';

import { omitTypename } from '@/graphql/utils/fields';

import { getTooltipOptions, getPersonFullLabel, getPersonLabel } from '@/components/tooltips/utils';

import BaseFormPresenterMixin from './BaseFormPresenterMixin';

const DEFAULT_RESULTS_PER_QUERY = 20;

export default {
  name: 'DropdownFormPresenter',
  components: {
    ZenMultiselect,
    InfiniteLoading,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  mixins: [BaseFormPresenterMixin],
  props: {
    value: {
      type: [Object, Array, String],
      default() {
        return [];
      },
    },
    allowEmpty: {
      type: Boolean,
      default: null,
    },
    formData: {
      type: Object,
      default() {
        return {};
      },
    },
    preInputLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dropdownOptions: [],
      querySearchValue: '',
      newTags: [],
    };
  },
  computed: {
    // These options came with the new Object request
    predefinedOptions() {
      const validOptions = isFunction(this.data.validOptions)
        ? this.data.validOptions(this.formData)
        : this.data.validOptions;
      return validOptions && validOptions.length ? validOptions : null;
    },
    options() {
      const options = this.predefinedOptions || this.dropdownOptions || [];
      const optionsAndTags = this.newTags.concat(options);
      return omitTypename(optionsAndTags);
    },
    isDropdownOptionsCompleted() {
      if (this.predefinedOptions || !this.optionsData || !this.optionsData.dropdownOptions) {
        return true;
      }

      const { totalCount, edges } = this.optionsData.dropdownOptions;
      return totalCount === edges.length;
    },
    cursor() {
      const length = this.optionsData.dropdownOptions.edges.length;
      if (length) {
        return this.optionsData.dropdownOptions.edges[length - 1].cursor;
      }
      return '';
    },
    isPersonDropdown() {
      return !!this.options.find(o => !!o.role);
    },
    isEmptyAllowed() {
      return isBoolean(this.allowEmpty) ? this.allowEmpty : !this.data.mandatory;
    },
    gqlVariables() {
      const optionsQueryFilter = get(this, 'extraInfo.optionsQueryFilter', '');
      const filter = isFunction(optionsQueryFilter) ? optionsQueryFilter(this.querySearchValue) : optionsQueryFilter;
      return { first: DEFAULT_RESULTS_PER_QUERY, after: '', filter };
    },
    disabled() {
      return this.optionsData == null && this.predefinedOptions == null;
    },
    loading() {
      return this.$apollo.loading;
    },
    taggable() {
      return this.data.name === 'tags';
    },
    multiple() {
      return (
        includes(
          [
            'multi',
            'tags',
            'auditors',
            'auditManagers',
            'owners',
            'ownersGroup',
            'people',
            'programOwners',
            'programEditors',
            'programReaders',
            'relatedAssignees',
            'relatedAssessors',
            'relatedCreators',
            'relatedRequesters',
            'relatedVerifiers',
            'reviewers',
          ],
          this.data.name
        ) ||
        (this.data.hasOwnProperty('customAttributeType') && this.data.customAttributeType === 'MULTI')
      );
    },
  },
  apollo: {
    optionsData: {
      fetchPolicy: 'cache-and-network',
      query() {
        return this.extraInfo.optionsQuery;
      },
      variables() {
        return this.gqlVariables;
      },
      skip() {
        return !this.extraInfo.optionsQuery;
      },
      error(error) {
        this.error = JSON.stringify(error.message);
      },
      update(data) {
        const parser = this.extraInfo.dropdownOptionsParser;

        this.dropdownOptions = parser ? parser(data) : data;
        return data;
      },
    },
  },
  methods: {
    getTooltipOptions,
    getPersonFullLabel,
    getPersonLabel,
    async fetchMore() {
      if (!this.isDropdownOptionsCompleted) {
        await this.$apollo.queries.optionsData.fetchMore({
          variables: { first: DEFAULT_RESULTS_PER_QUERY, after: this.cursor },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.dropdownOptions.edges;

            return {
              ...previousResult,
              dropdownOptions: {
                ...previousResult.dropdownOptions,
                edges: [...previousResult.dropdownOptions.edges, ...newEdges],
              },
            };
          },
        });
      }
      return Promise.resolve();
    },
    async infiniteHandler($state) {
      await this.fetchMore();
      $state.loaded();
      if (this.isDropdownOptionsCompleted) {
        $state.complete();
      }
    },
    searchHandler: debounce(function(value) {
      // Avoid triggering a GraphQL query if all the values are already downloaded
      if (this.isDropdownOptionsCompleted && this.querySearchValue === '') return;
      this.querySearchValue = value;
    }, 500),
    addTag(newTag) {
      const newTagObject = { value: newTag, label: newTag };
      this.newTags.push(newTagObject);
      this.$emit('input', this.value.concat(newTagObject));
    },
  },
};
</script>

<style lang="scss">
@import '~@/design/colors';

.dropdown-form-presenter {
  display: flex;
  flex-direction: column;

  &.is-invalid {
    .multiselect__tags {
      border-color: $color-amaranth;
      background-color: rgba($color-amaranth, 0.03);
    }
  }

  &__pre-input-lable {
    margin-right: 0.5rem;
    align-self: center;
    white-space: nowrap;
  }

  .is-invalid-error-message {
    display: block;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 80%;
    color: $color-amaranth;
  }
}
</style>
